import { format, fromUnixTime } from 'date-fns';
import {
  BLISTER_TYPE,
  DEFAULT_CUBE_POSITION,
  NOT_APPLICABLE_LABEL,
  PIT_TYPE,
  SliderType,
} from '@/constants';
import {
  TemplateData,
  TemplateField,
  EquipmentDetailOptionField,
  Maybe,
  EquipmentCardField,
  PoiStatus,
  FileTypes,
  WorkpackStatus,
  TextFormatEnum,
  AllAssemblyTemplatesForRiskAssessmentQuery,
  OptionData,
  TemplateFieldTypeEnum,
  NameAndArea,
  UnitsEnum,
  ImageType,
} from '@/__generated__/graphql';
import {
  AssemblyTemplateQuery,
  Columns,
  FormatFieldValueParams,
  ImageWithId,
  SortableField,
  SortDirection,
  SortType,
  VectorisedPointsWithHeight,
} from '@/types';
import { Attachment } from '@/components/Analysis/modules/pointOfInterest/PointOfInterestTab/SelectedPoiContent/types';
import {
  formatDisplay,
  formatInchesValue,
  formatPercentage,
  localiseMeasurement,
} from './unitSystem';
import JSZip from 'jszip';
import queryString from 'query-string';
import { BlisterFieldsData } from '@/components/Analysis/modules/pointOfInterest/PointOfInterestTab/SelectedPoiContent/hooks/useBlisterData';

export const ObjectIdRegex = new RegExp(/^[\dA-Fa-f]{24}$/);

/**
 *
 *  Function is used to join space in strings to _
 *  Primary purpose is to map Icon names to POI_ICON_MAP widget
 * @param name
 * @param splitToken
 * @param byToken
 * @returns Paint Damage -> Paint_Damage
 */
export const joinStringBy = (name: string, splitToken: string, byToken: string) => {
  if (name.length === 0) return name;
  const doesNameHaveSpace = name.split(splitToken).length > 0;
  if (doesNameHaveSpace) {
    return name.split(splitToken).join(byToken);
  }
  return name;
};

export const formatDate = (date: Date): string => {
  return date
    .toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      minute: '2-digit',
      hour: '2-digit',
    })
    .replace(',', '');
};

export function isEmail(email: string): boolean {
  // Regular expression pattern to validate email addresses
  const emailPattern = /^[\w%+.-]+@[\d.a-z-]+\.[a-z]{2,}$/i;

  return emailPattern.test(email);
}

export const convertTemplateNameToPoiType = (name: string | undefined): string => {
  if (name?.toUpperCase().includes(BLISTER_TYPE)) return BLISTER_TYPE;
  if (name?.toUpperCase().includes(PIT_TYPE)) return PIT_TYPE;
  return name?.toUpperCase()?.replaceAll(' ', '_') ?? '';
};

/**
 *
 * @param name Full Name
 * @returns Truncates last name eg Dow Zhong -> Dow Z
 */
export const formatName = (name: string): string => {
  if (!name) {
    return ''; // If name is empty or undefined, return an empty string
  }

  if (isEmail(name)) {
    // split at @ and return the first part
    // i.e john_doe3443@gmail -> john_doe3443
    return name.split('@')[0];
  }

  // Remove leading and trailing whitespace from the name
  const trimmedName = name.trim();

  // Split the trimmed name into an array of name parts using regular expression /\s+/ to handle multiple spaces
  const nameParts = trimmedName.split(/\s+/);

  // If there is only one name part, return the trimmed name as is
  if (nameParts.length === 1) {
    return trimmedName;
  }

  // Get the first name from the name parts array
  const firstName = nameParts[0];

  // Get the last name from the name parts array
  const lastName = nameParts.at(-1);

  // Concatenate the first name and the first character of the last name with a space in between
  return `${firstName} ${lastName?.charAt(0) || ''}`;
};

export const titleCaseSentence = <T>(string: T): T | string => {
  if (!string) {
    return '';
  }
  if (typeof string !== 'string') {
    return string;
  }
  return string.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
};

export const isWordAnAbbreviation = (word: string): boolean => {
  const isASentence = word.includes(' ');
  if (isASentence) return false;

  // Check if the word is all uppercase
  return word === word.toUpperCase();
};

/**
 * Converts a string to title case, handling both single words and sentences.
 *
 * This function takes a string as input and returns a new string where:
 *   - If the input is a single word, it's converted to title case (first letter capitalized, rest lowercase).
 *   - If the input is a sentence (contains spaces), each word is converted to title case, preserving common conjunctions and prepositions in lowercase.
 *
 * This function addresses potential shortcomings of previous implementations by:
 *   - Employing a separate `converter` function for clarity and reusability.
 *   - Accurately handling sentence case by checking for spaces and using `split` appropriately.
 *   - Maintaining consistency in capitalization rules for common words.
 *
 * @param string - The string to be converted to title case.
 * @returns {string} - The converted string in title case.
 *
 * @example
 * ```typescript
 * const singleWord = "hello";
 * const titleCaseSingleWord = toTitleCase(singleWord);
 * console.log(titleCaseSingleWord); // Output: "Hello"
 * ```
 *
 * @example
 * ```typescript
 * const sentence = "this is a sentence with some words";
 * const titleCaseSentence = toTitleCase(sentence);
 * console.log(titleCaseSentence); // Output: "This Is A Sentence With Some Words"
 * ```
 *
 * @example
 * ```typescript
 * const allCaps = "ALL CAPS HERE";
 * const titleCaseAllCaps = toTitleCase(allCaps);
 * console.log(titleCaseAllCaps); // Output: "All Caps Here" (Preserves original casing for non-words)
 * ```
 */
export const toTitleCase = (string: string): string => {
  const converter = (word: string) => {
    const isAbbreviation = isWordAnAbbreviation(word);

    return isAbbreviation ? word : word.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  };

  // Checks if its a multi word string
  const isSentence = string.includes(' ');

  if (isSentence) {
    const words = string.split(' ');
    return words.map(converter).join(' ');
  }

  return converter(string);
};

export function arrayEquals<T>(a: T[] | undefined, b: T[] | undefined, strict?: boolean): boolean {
  if (a === undefined && b === undefined) {
    return true;
  }
  if (a!.length === b!.length) {
    return a!.every((element, index) => {
      if (strict ? element === b![index] : b!.includes(element)) {
        return true;
      }

      return false;
    });
  }

  return false;
}

export function notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
  return value !== null && value !== undefined;
}

/**
 *
 * @param string Any string type input
 * @returns string type
 *          UpperCase the first character of the input `string` and lowercase the rest of the `string`
 *          If the input is null or undefined, return itself
 *          eg: LEVEL -> Level
 */
export function titleCase(string: string | null | undefined): string {
  return string ? string.charAt(0).toUpperCase() + string.slice(1).toLowerCase() : '';
}

export function getColorIndex(bsonId: string, numberOfColors: number): number | undefined {
  if (ObjectIdRegex.test(bsonId)) {
    const idInteger = Number.parseInt(bsonId.slice(0, 7) + bsonId.slice(18), 16);
    return idInteger % numberOfColors;
  }
  return undefined;
}

// Below panel ids for tagging have drag disabled,
// in future, if you can to disable some panel in tagging, add its panel Id here as well
export const taggingPanelsWithDragDisabled = [
  'isolated_part',
  'zone_selector',
  'visibility',
  'part_selector',
  'comments',
];

export const updateUrlQueryParameters = (key: string, value: string): void => {
  const url = new URL(window.location.href);
  url.searchParams.set(key, value);
  window.history.pushState({}, '', url.toString());
};

export const formatColor = (selectedColour: number) => {
  return `#${selectedColour.toString(16).slice(0, -2)}`; // formatting number to hex color
};

export const calculateCubePosition = ({
  width,
  height,
  cubeSize,
  padding = 50,
}: {
  width: number;
  height: number;
  cubeSize: number;
  padding?: number;
}): [number, number, number] => {
  if (width === Number.POSITIVE_INFINITY || height === Number.POSITIVE_INFINITY) {
    return DEFAULT_CUBE_POSITION;
  }

  if (width === Number.NEGATIVE_INFINITY || height === Number.NEGATIVE_INFINITY) {
    return DEFAULT_CUBE_POSITION;
  }

  if (width <= 0 || height <= 0) {
    return DEFAULT_CUBE_POSITION;
  }

  const x = width / 2 - cubeSize * 2 - padding;
  const y = height / 2 - cubeSize * 2 - padding;
  const z = 0;
  return [x, y, z];
};

// TODO: to be removed once key value pair flow is implemented
export function getFieldData(
  templateFields: Array<Partial<TemplateField>>,
  templateData: Array<TemplateData>
) {
  return templateFields?.map((field) => {
    const data = templateData?.find((item) => (item.templateFieldId || item.id) === field?.id);
    return {
      name: field?.name,
      value: data?.value,
      type: field?.type,
      position: 'position' in field ? field.position : undefined,
    };
  });
}

export function getTemplateFieldData(
  equipmentCard: Array<Partial<EquipmentDetailOptionField | EquipmentCardField>> | undefined,
  templateFieldData: Record<string, string>
) {
  return equipmentCard
    ? equipmentCard
        .map((card) => {
          if (card?.fieldKey) {
            return {
              name: card?.name,
              value: templateFieldData?.[card?.fieldKey] ?? '',
              type: card?.type,
              position: 'position' in card ? card.position : undefined,
              templateKey: card.fieldKey,
            };
          }
          return undefined;
        })
        .filter((item): item is NonNullable<typeof item> => item !== undefined) // type guard, NonNullable takes care of both null and undefined
    : [];
}

export const toFixedTwoDecimalPoints = (decimal: Maybe<string> | undefined): string | number => {
  try {
    if (!decimal) return 0;
    return Number.parseFloat(decimal).toFixed(2);
  } catch {
    return 0;
  }
};

/**
 * Attempts to detect the MIME type of a Blob by inspecting its magic bytes.
 *
 * @param blob - The Blob object to inspect.
 * @returns A promise that resolves to a guessed MIME type string.
 *
 * Supported formats:
 * - PNG (`image/png`)
 * - JPEG (`image/jpeg`)
 * - GIF (`image/gif`)
 * - Default: `application/octet-stream`
 * - check signature for JPEG and PNG
 */
const getMimeType = async (blob: Blob): Promise<string> => {
  const buffer = await blob.slice(0, 4).arrayBuffer();
  const bytes = new Uint8Array(buffer);

  if (bytes[0] === 0x89 && bytes[1] === 0x50) return 'image/png';
  if (bytes[0] === 0xff && bytes[1] === 0xd8) return 'image/jpeg';
  return 'application/octet-stream';
};

/**
 * @param url: string
 * @returns base64 string
 *         Converts the image url to base64 string
 *        Used for displaying the image in the pdf
 */
/**
 * Converts an image URL to a Base64 string.
 * Optionally attempts to fix the MIME type of the image if it may be incorrect.
 *
 * @param url - The image URL to fetch and convert.
 * @param fixMimeType - Whether to inspect and correct the MIME type (default: false).
 * @returns A promise that resolves to a Base64-encoded string of the image.
 *
 * @example
 * const base64 = await urlToBase64('https://s3.com/image', true);
 */
export const urlToBase64 = async (url: string, fixMimeType = false): Promise<string> => {
  const response = await fetch(url, {
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch image from ${url}`);
  }
  let blob = await response.blob();
  const isImage = /\.(jpe?g|png)$/i.test(url);
  if (fixMimeType && isImage) {
    const mime = await getMimeType(blob);
    blob = blob.slice(0, blob.size, mime);
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.addEventListener('error', reject);
    reader.readAsDataURL(blob);
  });
};

export const splitSort = (currentSort: SortableField) =>
  (currentSort?.split('-') as [SortType, SortDirection]) || [];

/**
 * Gets the next sortable field based on the currentSort and sortType.
 * If currentSort is not provided, it returns the default ascending sorting for the sortType.
 * If currentSort is provided and it's not of the same sortType, it returns the default ascending sorting for the new sortType.
 * If currentSort is provided and it's of the same sortType, it toggles between ascending and descending sorting.
 *
 * @param currentSort The current sorting applied to the field.
 * @param sortType The sortType to which the next sorting should be calculated.
 * @returns The next SortableField after applying the sorting logic, or undefined if sortType is invalid.
 */
export const getNextSort = (currentSort: SortableField, sortType: SortType): SortableField => {
  if (!currentSort) return `${sortType}-asc`;
  const [currentSortType, currentSortDirection] = splitSort(currentSort);
  if (currentSortType !== sortType) return `${sortType}-asc` as SortableField;
  if (currentSortDirection === 'asc') return `${sortType}-desc` as SortableField;
  return undefined;
};

export const isInfinity = (value: number) =>
  value === Number.POSITIVE_INFINITY || value === Number.NEGATIVE_INFINITY;

export const getGoverningValueCalculations = (
  type: string | undefined,
  blisterHeight: number,
  nominalCorrosionAllowance: number,
  nominalThickness: number,
  wallLossFactor?: number | null | undefined
) => {
  const DEFAULT_WALL_LOSS_FACTOR = 6;
  const MILLIMETERS_TO_METERS_FACTOR = 1000;
  const nominalCorrosionAllowanceInMeters =
    nominalCorrosionAllowance / MILLIMETERS_TO_METERS_FACTOR;
  const nominalThicknessInMeters = nominalThickness / MILLIMETERS_TO_METERS_FACTOR;

  const wallLoss =
    blisterHeight < 0 || type === PIT_TYPE
      ? blisterHeight
      : blisterHeight / (wallLossFactor ?? DEFAULT_WALL_LOSS_FACTOR);

  const wallLossPercentage = (wallLoss / nominalThicknessInMeters) * 100;

  const residualCorrosionAllowance = nominalCorrosionAllowanceInMeters - wallLoss;
  const residualCorrosionAllowancePercentage =
    (residualCorrosionAllowance / nominalCorrosionAllowanceInMeters) * 100;

  const residualThickness = nominalThicknessInMeters - wallLoss;
  const residualThicknessPercentage = (residualThickness / nominalThicknessInMeters) * 100;

  return {
    wallLoss: {
      value: wallLoss,
      percentage: isInfinity(wallLossPercentage) ? 0 : wallLossPercentage,
    },
    residualCorrosionAllowance: {
      value: nominalCorrosionAllowanceInMeters ? residualCorrosionAllowance : 0,
      percentage: isInfinity(residualCorrosionAllowancePercentage)
        ? 0
        : residualCorrosionAllowancePercentage,
    },
    residualThickness: {
      value: nominalThicknessInMeters ? residualThickness : 0,
      percentage: isInfinity(residualThicknessPercentage) ? 0 : residualThicknessPercentage,
    },
  };
};

/**
 * Fetches the second last item in an array, if it exists.
 * If the second last item doesn't exist, it returns the last item.
 *
 * @param array - The input array from which to retrieve the last non-null and non-undefined item.
 * @returns The last non-null and non-undefined item from the array, or undefined if no such item exists.
 *
 * @example
 * const array = [1,2,3,4]
 * const lastItem = getLastArrayItems(array); // Returns 3
 *
 */
export const getLastArrayItems = <T>(array: T[] | undefined): T | undefined => {
  if (!Array.isArray(array) || !array?.length || array?.length === 0) {
    return undefined;
  }

  // if all items are falsy, return undefined
  if (array.every((item) => !item)) {
    return undefined;
  }

  // try to get the second last item, if it doesn't exist, get the last item
  // otherwise return undefined
  return array?.[array.length - 2] ?? array?.[array.length - 1] ?? undefined;
};

/**
 * Gets the last two items from the array.
 *
 * @param array - The input array from which to retrieve the last two items.
 * @returns An array containing the last two items from the input array, or undefined if there are not enough items.
 *
 * @example
 * const array = [10, 20, 30, 40];
 * const lastTwoItems = getLastTwoArrayItems(array); // Returns [30, 40]
 *
 * const array = [1]
 * const lastTwoItems = getLastTwoArrayItems(array); // Returns [1]
 */
export const getLastTwoArrayItems = <T>(array: T[] | undefined): T[] | undefined => {
  const arrayLength = array?.length;

  if (!arrayLength) return undefined;

  if (arrayLength === 0) return undefined;
  if (arrayLength === 1) return [array[0]];
  return [array[arrayLength - 2], array[arrayLength - 1]];
};

/**
 * Deletes the specified query parameter from the current URL's query string
 * and updates the browser's history with the modified URL.
 *
 * @param parameterName - The name of the query parameter to be removed from the URL.
 *
 * @example
 * // Assuming the current URL is "https://example.com/page?foo=123&bar=456"
 * deleteQueryParameterFromUrl("foo");
 * // The URL will be modified to "https://example.com/page?bar=456"
 */
export const deleteQueryParameterFromUrl = (parameterName: string) => {
  const url = new URL(window.location.href);
  const parameters = new URLSearchParams(url.search);
  parameters.delete(parameterName);
  url.search = parameters.toString();
  window.history.replaceState({}, '', url.toString());
};

export const calculatePercentage = (area: number, value: number) => {
  try {
    return Math.round((value / area) * 100);
  } catch {
    return 0;
  }
};

export const clamp = (value: number, minValue: number, maxValue: number): number => {
  return Math.max(Math.min(value, maxValue), minValue);
};

export const toHexString = (n: number, resultLength: number) => {
  return Math.trunc(n).toString(16).toLowerCase().padStart(resultLength, '0');
};

export const colorWithAlpha = (color: number, blendOpacity: number): number => {
  const alpha = clamp(Math.trunc(blendOpacity), 0, 255);

  return (color & 0xffffff00) | (alpha & 0xff);
};

export const convertColorHexStringToNumber = (colorString: string): number => {
  switch (colorString.length) {
    case 9:
      // '#11223344'
      return Number.parseInt(colorString.slice(1), 16);
    case 7:
      // '#112233'
      return Number.parseInt(colorString.slice(1), 16) * 256 + 255;
    default:
      throw new Error(`Unsupported color string '${colorString}'`);
  }
};

export const convertRgbaToRgb = (rgba: number): number => {
  return Math.trunc(rgba / 0x100);
};

export const convertRgbToRgba = (rgb: number, alpha: number = 0xff): number => {
  return rgb * 0x100 + alpha;
};

export const convertRgbToColorHexString = (rgb: number): string => {
  return `#${toHexString(rgb, 6)}`;
};

export const convertRgbaToColorHexString = (rgba: number): string => {
  return `#${toHexString(rgba, 8)}`;
};

export const hexColorWithAlpha = (color: string, alpha: number): string => {
  const number = convertColorHexStringToNumber(color);
  const rgba = colorWithAlpha(number, alpha);
  return convertRgbaToColorHexString(rgba);
};

export const hexToRgba = (hex: string, a = 0.5) => {
  const [r, g, b] = [0, 2, 4].map((index) => Number.parseInt(hex.slice(index + 1, index + 3), 16));
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

export const getEquipmentColumnsFromAssemblyTemplate = (
  assemblyTemplate:
    | AssemblyTemplateQuery
    | AllAssemblyTemplatesForRiskAssessmentQuery['allTemplates'][0]
): Columns[] => {
  return assemblyTemplate.fields
    .filter(({ displayInTable }) => {
      if (displayInTable === undefined || displayInTable === null) return true;
      else return displayInTable;
    })
    .map(
      (
        {
          id,
          name,
          displayConfig,
          key,
          displayInQuery,
          displayInRisk,
          roundToDecimal,
          unit,
          displayUnit,
          optionsData,
        },
        index
      ) => ({
        isSortable: displayConfig?.isSortable || false,
        visibility: index < 4, // initially only 3 dynamic should be visible in table
        key: key ?? id,
        templateFieldKey: key ?? '',
        sortKey: `templateFieldData.${key}`,
        sortType: displayConfig?.sortType,
        columnName: name,
        isTemplateField: true,
        displayConfig: {
          color: displayConfig?.color ?? undefined,
          type: displayConfig?.type ?? 'TEXT',
        },
        displayInQuery,
        displayInRisk,
        roundToDecimal,
        unit,
        displayUnit,
        optionsData: optionsData ?? undefined,
      })
    );
};

export const removeMaybeTypeFromValue = <T>(value: Maybe<T>): T | undefined => {
  if (!value) return undefined;
  return value;
};

export const isNullOrUndefined = <T>(value: T | null | undefined): value is null | undefined => {
  return value === null || value === undefined;
};

export const roundFloat = (value: number, decimalPlaces: number) => {
  return value.toFixed(decimalPlaces);
};

export const formatTimeStamps = (timeStamp: string | number, dateFormat: string) => {
  const converetdTimeStamp = typeof timeStamp === 'string' ? Number(timeStamp) : timeStamp;
  const date = format(fromUnixTime(converetdTimeStamp / 1000), dateFormat);
  return date;
};

export const joinByRecommendationKey = (template: AssemblyTemplateQuery) => {
  return template?.fields.filter((field) => {
    const isKeyFoundInRecomendation = template?.recommendation?.details.find((detail) => {
      return detail?.fieldKey === field.key;
    });
    return isKeyFoundInRecomendation;
  });
};

export const formatRecommendationHeader = (header: string, isUpdateMode: boolean) => {
  const updateVerb = isUpdateMode ? 'Update' : 'Add';
  return `${updateVerb} ${header}`;
};

/**
 * Checks if a string is undefined, empty, or contains only whitespace.
 *
 * @param {string | undefined} value - The string to be checked.
 * @returns {boolean} Returns true if the string is undefined, empty, or contains only whitespace; otherwise, returns false.
 *
 * @example
 * const result = checkUndefinedString("   "); // Returns true
 * const result = checkUndefinedString("Hello World"); // Returns false
 * const result = checkUndefinedString(undefined); // Returns true
 */
export const isStringFalsy = (value: string | undefined) => {
  if (value === undefined) {
    return true;
  }

  // if string does not contain any characters or only contains whitespace
  const trimmedValue = value.replace(/\s/g, '');

  if (trimmedValue === '') {
    return true;
  }

  return value === 'undefined';
};

export const extractStatusCodeFromApolloResponse = (response: unknown): number | undefined => {
  if (!response || typeof response !== 'object' || !('statusCode' in response)) {
    return undefined;
  }

  const numericStatusCode = Number(response.statusCode);
  return Number.isNaN(numericStatusCode) ? undefined : numericStatusCode;
};

export const convertTimeStampToDate = (timeStamp: Maybe<string> | undefined): Date | undefined => {
  if (!timeStamp) return undefined;
  return new Date(Number(timeStamp));
};

/**
 * Accepts:
 *   • 10-digit seconds  (e.g. 1712107543)
 *   • 13-digit millis   (e.g. 1712107543183)
 * Returns true **only** if the value maps to a valid JS Date.
 */
export const isTimeStamp = (value: string | number): boolean => {
  const num = typeof value === 'number' ? value : Number(value);

  // must be a finite integer
  if (!Number.isFinite(num) || !Number.isInteger(num)) return false;

  // Convert seconds → millis if necessary
  const ms = num < 1e12 ? num * 1000 : num;

  // JS Date valid range check
  if (ms < 0 || ms > 8_640_000_000_000_000) return false;

  // Round-trip through Date to be absolutely sure
  return new Date(ms).getTime() === ms;
};

/**
 *
 * @param string Any string type input
 * @returns string type
 *          Replaces underscores with spaces and UpperCase the first character of the each word in input `string` and lowercase the rest of the word
 *          If the input is null or undefined, return itself
 *          eg: LEVEL_TWO -> Level Two
 */
export function formatEnumForDisplay(string: string | null | undefined): string {
  return string ? titleCaseSentence(string.replaceAll('_', ' ')) : '';
}

export const formatPoiStatus = (status: PoiStatus | WorkpackStatus) => {
  if (!status) return 'No Status';
  // Handle special cases first
  if (status.toString() === 'INPROGRESS') return 'In Progress';
  if (status.toString() === 'INREVIEW') return 'In Review';
  return formatEnumForDisplay(status) ?? '';
};

/**
 *
 * @param string Any string type input
 * @returns string type
 *          Replaces underscores with spaces and UpperCase the first character of each word in input `string` and lowercase the rest of the word
 *          If the input is null or undefined, return itself
 *          eg: LEVEL_TWO -> Level Two or LevelTwo -> Level Two
 */
export function formatKeyForDisplay(
  string: string | null | undefined,
  titleCase: boolean = true
): string {
  if (!string) return '';

  // Replace underscores with spaces and handle camel case
  const formattedString = string.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2');
  if (!titleCase) return formattedString;
  return titleCaseSentence(formattedString);
}

/**
 *
 * @param string Any string type input
 * @returns string type
 *          Removes all the spaces in the string and then convert to upper case
 *          eg: Level Two -> LEVELTWO
 */
export function formatRemoveSpaceUpperCase(string: string | null | undefined): string {
  return string ? string.replace(/\s+/g, '').toLocaleUpperCase() : '';
}

/**
 *
 * @param string Any string type input
 * @returns string type
 *          Replaces space with underscores and UpperCases the entire string
 *          If the input is null or undefined, return itself
 *          eg: Level Two -> LEVEL_TWO
 */
export function upperSnakeCase(string: string | null | undefined): string {
  return string ? string.replaceAll(' ', '_').toLocaleUpperCase() : '';
}

const statuses = ['No Status', ...Object.values(PoiStatus).map((item) => formatPoiStatus(item))];

export const formatDropdownValue = (
  value: string | undefined | null,
  valueFormat: Maybe<TextFormatEnum>,
  defaultValue = 'N/A'
) => {
  if (!value) return defaultValue;

  // Format PoiStatus with mapping
  const poiStatus = formatPoiStatus(value as PoiStatus);
  if (statuses.includes(poiStatus)) return poiStatus;

  switch (valueFormat) {
    case TextFormatEnum.None:
      return value;
    case TextFormatEnum.UpperCase:
      return value.toUpperCase();
    case TextFormatEnum.LowerCase:
      return value.toLowerCase();
    case TextFormatEnum.TitleCase:
      return titleCase(value);
    default:
      return value;
  }
};

/*
Function that takes an object and sets all properties to false
except for the provided property, which will be toggled false if true, true if false.
*/
export function toggleValue<T>(drawerState: T, property: keyof T): T {
  const newState: Partial<T> = { ...drawerState };
  (Object.keys(newState) as Array<keyof T>).forEach((key) => {
    if (key === property) {
      newState[key] = !newState[key] as T[keyof T];
    } else {
      newState[key] = false as T[keyof T];
    }
  });
  return newState as T;
}

/*
Function that takes an object and sets all properties to false
except for the provided property, which will be toggled false if true, true if false.
*/
export function closeAll<T>(drawerState: T): T {
  const newState: Partial<T> = { ...drawerState };
  (Object.keys(newState) as Array<keyof T>).forEach((key) => {
    newState[key] = false as T[keyof T];
  });
  return newState as T;
}
export const detectOs = () => {
  const os = ['Win', 'Mac', 'Linux'];
  return os.find((v) => window?.navigator.platform.indexOf(v) >= 0);
};

export const getScale = () => {
  switch (detectOs()) {
    case 'Win':
      return 1;
      break;
    case 'Mac':
      return 1;
      break;
    default:
      return 1;
  }
};

/**
 * Initiates the download of a file from a given URL.
 * @param url - The URL of the file to be downloaded.
 * @param fileName - The name to be given to the downloaded file.
 */
export const downloadFile = async (url: string, fileName: string): Promise<void> => {
  try {
    const response = await fetch(url, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch file from ${url}`);
    }
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = blobUrl;
    anchor.download = fileName;
    anchor.target = '_blank';
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
  } catch (error) {
    console.error('Error downloading file:', error);
  }
};

/**
 * Checks if the Attachment is an image file.
 * @param file - Attachment to determine if image.
 * @returns boolean or undefined type
 */
export const isImageFile = (file: Attachment) => {
  if (!file) return undefined;
  return (
    (file?.type.includes('image/') ||
      [FileTypes.Jpeg, FileTypes.Png].includes(file.type as FileTypes)) ??
    false
  );
};

export const getRandomItem = (array: number[]) => array[Math.floor(Math.random() * array.length)];

export const upscaleGoogleContentImage = (imageUrl: string, resolution?: number) => {
  // https://lh3.googleusercontent.com/a/XXXXXXXXX=s96-c
  // replace the =s96-c with =s${resolution ?? 800}-c
  // The s parameter specifies the maximum height or width of the image.
  // By default the images are lower resolution, so we need to upscale them
  return imageUrl.replace(/=s\d+-c/, `=s${resolution ?? 800}-c`);
};

/**
 * @param url: string
 * @returns SVG string
 *         Converts the image url to SVG string
 *         Used for displaying the POI icons in 3D viewer
 */
export const urlToSVG = async (url: string): Promise<string> => {
  if (!url) return '';
  const response = await fetch(url, {
    credentials: 'include',
  });

  if (!response.ok) {
    return '';
  }
  const svg = await response.text();
  return svg;
};

export function formatNumberForDisplay(value: string | number | null | undefined): string {
  const formattedValue = value && typeof value === 'string' ? Number(value) : value;

  return Number.isNaN(formattedValue) ? 'N/A' : formattedValue?.toLocaleString() ?? '';
}

export const parseNumberOrInfinity = (value: string | undefined) => {
  return Number.isNaN(Number.parseFloat(value || '0'))
    ? Number.POSITIVE_INFINITY
    : Number.parseFloat(value || '0');
};

export const isNumber = (value: unknown): boolean => {
  return typeof value === 'number' && !Number.isNaN(value);
};

/**
 * Normalizes a value from the range [0, 10] to the range [-0.8, 0.8]
 * @param value - The input value to normalize (between 0 and 10)
 * @returns The normalized value between -0.8 and 0.8
 */
export const normalizeSliderValues = (value: number, sliderType: string): number => {
  if (value < 0 || value > 100) {
    throw new Error('Input value must be between 0 and 100');
  }
  if (sliderType === SliderType.BRIGHTNESS) {
    const normalizedToZeroOne = value / 100;
    const scaled = normalizedToZeroOne * 1.6;
    return scaled - 0.8;
  } else {
    const normalizedToZeroOne = value / 100;
    const scaled = normalizedToZeroOne * 2;
    return scaled - 1;
  }
};

/**
 * Denormalizes a value from the range [-0.8, 0.8] back to the range [0, 10]
 * @param normalizedValue - The input value to denormalize (between -0.8 and 0.8)
 * @returns The denormalized value between 0 and 10
 */
export const denormalizeSliderValues = (normalizedValue: number, sliderType: string): number => {
  if (sliderType === SliderType.BRIGHTNESS) {
    if (normalizedValue < -0.8 || normalizedValue > 0.8) {
      throw new Error('Input value must be between -0.8 and 0.8');
    }
    const shifted = normalizedValue + 0.8;
    const normalizedToZeroOne = shifted / 1.6;
    return normalizedToZeroOne * 100;
  } else {
    if (normalizedValue < -1 || normalizedValue > 1) {
      throw new Error('Input value must be between -1 and 1');
    }
    const shifted = normalizedValue + 1;
    const normalizedToZeroOne = shifted / 2;
    return normalizedToZeroOne * 100;
  }
};

export const getCoverageLevel = (percentage: number, optionsData?: Maybe<Array<OptionData>>) => {
  if (!optionsData || percentage === null) return null;

  for (const entry of optionsData) {
    if (
      typeof entry !== 'object' ||
      typeof entry.value !== 'string' ||
      typeof entry.min !== 'number' ||
      typeof entry.max !== 'number' ||
      entry.min > entry.max
    ) {
      continue;
    }

    if (percentage >= entry.min && percentage <= entry.max) {
      return entry.value;
    }
  }

  return null;
};

export const convertMilliSecondToFormattedTime = (timeInMs: number): string => {
  const totalSeconds = Math.floor(timeInMs / 1000);

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const parts: string[] = [];

  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (hours === 0) parts.push(`${seconds}s`);
  if (parts.length > 0) return parts.join(' ');
  return 'N/A';
};

export const formatFieldValue = ({
  unitSystem,
  type,
  unit,
  value,
  showUnitLabel = true,
  isBooleanField = false,
  displayUnit,
  roundToDecimal,
  percentage,
  fallbackLabel = NOT_APPLICABLE_LABEL,
}: FormatFieldValueParams) => {
  const { MeasurementPaintLength, MeasurementPaintDiameter, MeasurementPaintArea } =
    TemplateFieldTypeEnum;
  let squareMeterValue = false;
  if (
    type &&
    [MeasurementPaintLength, MeasurementPaintDiameter, MeasurementPaintArea].includes(type)
  ) {
    if (!value?.length || isNaN(Number(value))) {
      return fallbackLabel;
    }
    if (type === TemplateFieldTypeEnum.MeasurementPaintLength) {
      unit = UnitsEnum.Meter;
      showUnitLabel = true;
    }
    if (type === TemplateFieldTypeEnum.MeasurementPaintDiameter) {
      return formatInchesValue(Number(value), showUnitLabel);
    } else if (type === TemplateFieldTypeEnum.MeasurementPaintArea) {
      squareMeterValue = true;
      unit = UnitsEnum.SquareMeter;
    }
  }

  if (isBooleanField) {
    return value && value === 'true' ? 'Yes' : 'N/A';
  } else if (unit) {
    const convertedValue =
      localiseMeasurement({
        unitSystem,
        sourceUnit: squareMeterValue ? UnitsEnum.SquareMeter : unit,
        displayAsUnit: displayUnit,
        value: Number(value),
        roundToDecimal: roundToDecimal ?? 2,
        showUnitLabel,
        fallbackLabel,
      }) ?? value;
    const percentageText = percentage ? formatPercentage(percentage) : '';
    return convertedValue + percentageText;
  } else if (roundToDecimal) {
    return formatDisplay(Number(value), '', roundToDecimal);
  }

  return value;
};

export const getTotalCorrosionArea = (
  defects: Array<string> | undefined,
  area: NameAndArea | undefined
) => {
  return area?.totalArea?.reduce((accumulator, currentValue) => {
    return (
      accumulator +
      (defects?.includes(currentValue?.type.toLowerCase() ?? '') ? currentValue?.value ?? 0 : 0)
    );
  }, 0);
};

export function sortPoiAttachments(
  allImages: ImageWithId[],
  primaryImage: ImageWithId | undefined
): ImageWithId[] {
  const images = [...allImages];

  const primaryIdx = primaryImage?.id
    ? images.findIndex((img) => img.id === primaryImage.id)
    : images.findIndex((img) => img.imageType === ImageType.Primary);

  if (primaryIdx > -1) {
    const [primaryImg] = images.splice(primaryIdx, 1);
    return [primaryImg, ...images];
  }

  const typeOrder: Record<string, number> = {
    primary: 0,
    attachment: 1,
    viewpoint: 2,
  };

  return images.sort(
    (a, b) =>
      (typeOrder[a.imageType?.toLowerCase?.() ?? ''] ?? 3) -
      (typeOrder[b.imageType?.toLowerCase?.() ?? ''] ?? 3)
  );
}

export const createPdfLink = (blob: Blob, fileName: string) => {
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
};

export const generateZipFile = (
  base64Data: Array<string | null>,
  selectedReportIds: string[],
  selectedReportsMap: Record<string, string>
) => {
  const zip = new JSZip();
  base64Data.forEach((base64Data, index) => {
    if (base64Data) {
      // Generate filename based on report index and name if available
      const reportId = selectedReportIds.at(index);
      const fileName = `${
        reportId && selectedReportsMap[reportId]
          ? selectedReportsMap[reportId]
          : `report_${index + 1}`
      }.pdf`;
      zip.file(fileName, base64Data, { base64: true });
    }
  });
  zip
    .generateAsync({ type: 'blob' })
    .then((content) => {
      // Download the zip file
      createPdfLink(
        content,
        selectedReportIds.length > 1 ? `reports_${selectedReportIds.length}.zip` : 'report.zip'
      );
    })
    .catch((error) => {
      console.error('Error generating zip file:', error);
    });
};

export const titleCaseFromKey = (key: string) =>
  key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .split(/[\s\-_]+/g)
    .filter(Boolean)
    .map((w) => w[0]?.toUpperCase() + w.slice(1))
    .join(' ');

export const updateUrl = (key: string, value: string | string[] | undefined | null) => {
  const parsed = queryString.parse(window.location.search, { arrayFormat: 'comma' });

  if (value && value !== '[]') {
    parsed[key] = value;
  } else {
    delete parsed[key];
  }

  const newUrl = `${window.location.pathname}?${queryString.stringify(parsed, {
    arrayFormat: 'comma',
  })}`;

  window.history.replaceState({}, 'Abyss Fabric', newUrl);
};

export const findHighestPoint = (points0: VectorisedPointsWithHeight) => {
  let highestPointIndex = undefined;
  let highestHeight = 0;

  points0.height.forEach((pointHeight, index) => {
    if (pointHeight > highestHeight) {
      highestPointIndex = index;
      highestHeight = pointHeight;
    }
  });
  return highestPointIndex;
};

/**
 * Finds the index of the value in the points array that is closest to the target height
 * (from blisterData), within a given tolerance.
 * @param points - Array of points (number[])
 * @param blisterData - BlisterFieldsData object
 * @param precision - Number of decimal places to use for comparison (default: 3)
 * @returns Index of the closest height within tolerance, or undefined
 */
export const findBlisterHeightIndex = (
  points: number[],
  unitScaleFromRawToDisplay: number,
  blisterData?: BlisterFieldsData,
  precision: number = 2
): number | undefined => {
  const tolerance = Math.pow(10, -precision) * unitScaleFromRawToDisplay;
  const { manualSetHeight, estimatedHeight } = blisterData || {};
  const targetHeight = manualSetHeight
    ? Number(manualSetHeight)
    : estimatedHeight
    ? Number(estimatedHeight) * unitScaleFromRawToDisplay
    : undefined;

  if (targetHeight === undefined || Number.isNaN(targetHeight)) return;

  let closestIndex: number | undefined;
  let minDiff = Infinity;

  for (let i = 0; i < points.length; i++) {
    const diff = Math.abs(points[i] - targetHeight);
    if (diff <= tolerance && diff < minDiff) {
      minDiff = diff;
      closestIndex = i;
    }
  }

  return closestIndex;
};
